import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { isObservable, Observable, of, Subscription } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { RequiredDirective } from "../../directives/required/required.directive";
import { ValidationMessageComponent } from "../../validation/validation-message/validation-message.component";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { CheckboxComponent } from "../checkbox/checkbox.component";
import { InfoComponent } from "../info/info.component";
import { SelectionItem } from "./selection-item";
import { SelectionStrategySelectFirstLeafIfOnlyOne } from "./selection-strategies/SelectionStrategySelectFirstLeafIfOnlyOne";
import { SelectionEvent } from "./SelectionEvent";
import { SelectionEventType } from "./SelectionEventType";
import { SelectionStrategyType } from "./SelectionStrategyType";
import { TreeSelectorService } from "./tree-selector.service";
import { ValueStrategyType } from "./ValueStrategyType";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TreeSelectorComponent)
        },
        {
            multi: false,
            provide: ControlValueAccessorBaseComponent,
            useExisting: forwardRef(() => TreeSelectorComponent)
        }
    ],
    selector: "rask-tree-selector",
    styleUrls: ["./tree-selector.component.scss"],
    templateUrl: "./tree-selector.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RequiredDirective,
        InfoComponent,
        FormsModule,
        NgClass,
        NgTemplateOutlet,
        ValidationMessageComponent,
        CheckboxComponent
    ]
})
export class TreeSelectorComponent extends ControlValueAccessorBaseComponent<SelectionItem[]> implements OnInit, OnDestroy {
    @Input() autoSort = false;
    @Input() disableDeleteAll = false;
    @Input() emptyText: string;
    gotChildren = false;
    @Input() manualItemSync = false;
    observableItems: Observable<SelectionItem[]>;
    @Input() placeholder: string;

    // eslint-disable-next-line @angular-eslint/no-output-rename,  @angular-eslint/no-output-on-prefix
    @Output("onSelectionEvent") selectionEvents = new EventEmitter<SelectionEvent>();
    selectionItems: SelectionItem[];
    @Input() selectionStrategyType = SelectionStrategyType.SelectAll;
    show = false;
    @Input() showHeader = false;
    @Input() syncOnClear = false;
    @Input() valueStrategyType = ValueStrategyType.SelectAll;
    @Input() visibilityControlledByCaller = false;
    @Input() onBeforeSelectionEvent: ($event: SelectionEvent) => Promise<boolean>;

    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly elementRef = inject(ElementRef);
    private readonly treeSelectorService = inject(TreeSelectorService);
    private readonly zone = inject(NgZone);

    private ignoreNextClickToggledByCaller: boolean; // indicates to clickout if act or not on event
    private onBeforeSelectionValidationInProgress = false;
    private subscription: Subscription;

    get displayValue(): string | undefined {
        const value = this.treeSelectorService
            .getSelectedRoots(this.selectionItems, this.valueStrategyType)
            .map(i => this.displayValueFormatter(i)).join(", ");

        return value === "" ? undefined : value;
    }

    get hasValue(): boolean {
        return this.value != null && this.value.length > 0;
    }

    @Input()

    get items() {
        return this.observableItems;
    }

    set items(itemList: Observable<SelectionItem[]> | SelectionItem[]) {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        this.observableItems = isObservable(itemList) ? itemList : of(itemList);
        this.subscription = this.observableItems.subscribe(i => {
            this.selectionItems = i;
            this.gotChildren = this.selectionItems.some(si => si.children);

            if (ObjectUtils.hasValue(this.value)) {
                this.syncItems();
                this.updateValues();
            }
        });
    }

    clickout = (event) => {
        if (this.show && !this.onBeforeSelectionValidationInProgress && !this.elementRef.nativeElement.contains(event.target)) {
            if (this.visibilityControlledByCaller) {
                if (!this.ignoreNextClickToggledByCaller) {
                    this.show = false;
                }

                this.ignoreNextClickToggledByCaller = undefined;
            } else {
                this.show = false;
            }
        }
        this.cdRef.detectChanges();
    };

    @Input() comparePredicate = (a, b) => a === b;

    delete(): void {
        this.value.length = 0;
        this.syncItems([], this.syncOnClear);
        this.triggerChanges();
    }

    @Input() displayValueFormatter = (i: SelectionItem) => i.text;

    formatter = (val: SelectionItem[]): any[] => {
        if (val !== undefined) {
            return val.map(v => v.value);
        }

        return val;
    };

    getCaretClass(item: SelectionItem): string[] {
        const classList: string[] = [];

        if (!this.gotChildren) {
            return ["hidden"];
        }

        if (item.children && item.expanded) {
            classList.push("fa-angle-up");
        } else if (item.children && !item.expanded) {
            classList.push("fa-angle-down");
        }

        return classList;
    }

    getTextClass(item: SelectionItem): string {
        let textClass = "text-color-active";
        const inactiveClass = "text-color-inactive";

        if (item.aktiv === false) {
            textClass = inactiveClass;
        }

        if (item.parent?.aktiv === false) {
            textClass = inactiveClass;
        }

        if (item.parent?.parent?.aktiv === false) {
            textClass = inactiveClass;
        }

        return textClass;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        document.removeEventListener("click", this.clickout);
    }

    ngOnInit() {
        if (this.required === undefined) {
            this.required = false;
        }

        if (this.autoSort && this.sortExpression) {
            this.sort(this.selectionItems);
        }

        this.zone.runOutsideAngular(() => {
            document.addEventListener("click", this.clickout);
        });
    }

    onBeforeChangeFactory = (item: SelectionItem) => async (checked: boolean): Promise<boolean> => {
        if (!this.onBeforeSelectionEvent) {
            return true;
        }

        let validationResult: boolean;
        try {
            const selectionEvent = new SelectionEvent(checked ? SelectionEventType.Checked : SelectionEventType.Unchecked, item.value);
            this.onBeforeSelectionValidationInProgress = true;
            validationResult = await this.onBeforeSelectionEvent(selectionEvent);

        } finally {
            this.onBeforeSelectionValidationInProgress = false;
        }

        return validationResult;
    };

    onItemChange(item: SelectionItem): void {
        if (this.disabled) {
            return;
        }

        const actions = this.treeSelectorService.updateItemRelations(item, this.selectionItems, this.selectionStrategyType);
        actions.forEach(g => {
            this.selectionEvents.emit(g);
        });
        this.updateValues();
    }
    @Input() sortExpression: (a: SelectionItem, b: SelectionItem) => number = (a, b) => a.text.localeCompare(b.text, "sv");

    toggle(): void {
        if (this.disabled) {
            return;
        }

        if (this.visibilityControlledByCaller) {
            this.ignoreNextClickToggledByCaller = true;
        }

        this.show = !this.show;
        switch (this.selectionStrategyType) {
            case SelectionStrategyType.SelectAll: {
                break;
            }
            case SelectionStrategyType.SelectFirstLeafIfOnlyOne: {
                SelectionStrategySelectFirstLeafIfOnlyOne.onToggle(this.show, this.selectionItems);
                break;
            }
        }
    }

    toggleTree(item: SelectionItem): void {
        item.expanded = !item.expanded;
    }

    writeValue(value: any): void {
        if (value !== undefined && value !== null) {
            if (!Array.isArray(value)) {
                value = [value];
            }
            this.syncItems(value);
            this.updateValues();
        }
    }

    private sort(items: SelectionItem[]) {
        items?.sort((a, b) => this.sortExpression(a, b));
        items?.forEach(item => {
            this.sort(item.children);
        });
    }

    private syncItems(value?: any, syncOnClear = false): void {
        if (this.manualItemSync && !syncOnClear) {
            return;
        }

        if (value === undefined) {
            value = this.formatter(this.value);
        }

        this.treeSelectorService
            .syncSelectionItems(value, this.selectionItems, this.comparePredicate, this.selectionStrategyType, false);
    }

    private updateValues(): void {
        this.value = this.treeSelectorService.getSelectedItems(this.selectionItems, this.valueStrategyType);
        if (this.autoSort && this.sortExpression) {
            this.sort(this.selectionItems);
        }
    }
}
